/**
 * FAQ section template engine.
 *
 * Ported from faqs.me (`src/lib/engine/index.ts`), whose 27 templates live in
 * ./templates. One function turns a section's FAQs and design into scoped
 * HTML + CSS, and every surface calls it: the embed on a customer's page
 * (site/embeddables/templated.js), the share page, and the dashboard's design
 * preview. So the preview is the embed, by construction.
 *
 * What changed on the way over from faqs.me:
 *
 *  - Answers are HTML the server already sanitised (the reader's copy of an
 *    FAQ article), not faqs.me's markdown-lite. Templates never see them: each
 *    gets a placeholder comment, and the engine swaps the answers in after the
 *    template has rendered and its ids have been scoped. The search templates
 *    read `searchText`, which is escaped, instead of stripping tags off the
 *    answer inside an attribute.
 *  - Ids are scoped to the mount. faqs.me assumes one section per page; a
 *    customer can embed two, or the same one twice, and `aria-controls` must
 *    name exactly one element.
 *  - The fixed copy a template carries ("Most asked", "Still need help?") comes
 *    from `content.labels`, translated by the server in the reader's language
 *    and overridable per help center like every other site string.
 *  - No paywall teasers, attribution or JSON-LD: an embedded section is the
 *    help center's own content, and it renders with JS, so it promises no SEO.
 */

import { ICONS, ICON_OPEN_TRANSFORM, MISC_SVG } from './icons';
import { escapeHtml, htmlToText } from './escape';
import { getTemplate } from './templates';

export const ICON_STYLES = ['chevron', 'plus', 'arrow', 'none'];
export const HEAD_FONTS = ['auto', 'system', 'brand', 'serif', 'page'];
export const THEMES = ['light', 'dark', 'auto'];
export const RADIUS_MIN = 0;
export const RADIUS_MAX = 24;
export const RADIUS_DEFAULT = 12;

const FONTS = {
    system: "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
    brand: "'Bricolage Grotesque', system-ui, -apple-system, sans-serif",
    serif: "'Source Serif 4', Georgia, 'Times New Roman', serif",
    mono: "'SFMono-Regular', Consolas, Menlo, monospace",
};

/**
 * The webfonts behind `brand` and `serif`, served by Bunny Fonts — the same
 * privacy-respecting mirror the v2 help center loads its fonts from, so a
 * visitor's address never reaches Google. A page whose CSP refuses the host
 * falls back to the system stacks above.
 */
const FONT_FAMILIES = {
    brand: 'bricolage-grotesque:400,500,600,700,800',
    serif: 'source-serif-4:400,400i,600,700',
};

/** Heading font per template when the design says `auto`. */
const DEFAULT_HEAD = {
    slate: 'system', glass: 'brand', bubble: 'brand', ledger: 'serif', duet: 'system',
    pivot: 'brand', atlas: 'system', haiku: 'system', trail: 'brand', beacon: 'system',
    blok: 'brand', prism: 'brand', terminal: 'system', marquee: 'serif', orbit: 'brand',
    mosaic: 'system', ticket: 'brand', finder: 'system', folio: 'serif', neon: 'brand',
    rail: 'brand', weave: 'system',
    cove: 'brand', postcard: 'serif', bulletin: 'system', dialogue: 'brand', canopy: 'serif',
};

/**
 * The fixed copy templates carry, in English. The server sends these
 * translated (`FaqSectionDesign::labels()`); this is the fallback for a
 * payload that predates a label.
 */
export const DEFAULT_LABELS = {
    general: 'General',
    search: 'Search questions and answers',
    searchCount: 'Search :count answers\u2026',
    mostAsked: 'Most asked',
    results: 'Results',
    noResults: 'No answers match your search. Try a different word.',
    contactTitle: 'Still need help?',
    contactText: 'Can\u2019t find the answer you\u2019re looking for? Our team is happy to help.',
    contactCta: 'Contact us',
    helpCenterCta: 'Visit our help center',
    previous: 'Previous question',
    next: 'Next question',
    position: ':current of :total',
    youAsk: 'You ask',
    weAnswer: 'We answer',
    qa: 'Q & A',
    questionsOne: ':count question',
    questionsMany: ':count questions',
    scroll: 'Scroll',
};

export function hexToRgba(hex, alpha) {
    const h = (hex || '#4a6cf7').replace('#', '');
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const n = parseInt(full, 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

const isHex = (value) => typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);

/** Only links a visitor can follow safely; anything else falls back. */
export function safeLink(url) {
    const value = typeof url === 'string' ? url.trim() : '';
    return /^(https?:\/\/|mailto:)/i.test(value) && !/[\s<>"']/.test(value) ? value : '';
}

/**
 * A stored design, completed with the template's defaults. Mirrors
 * `FaqSectionDesign::RULES` on the server, which is what keeps a stored
 * value inside these sets in the first place.
 *
 * @param {{defaultAccent: string}} template
 * @param {Object} [design]
 * @param {'custom'|'full'} [sectionType]
 */
export function resolveDesign(template, design, sectionType) {
    const stored = design && typeof design === 'object' ? design : {};
    const radius = Number.isInteger(stored.radius) ? stored.radius : RADIUS_DEFAULT;

    return {
        accent: isHex(stored.accent) ? stored.accent.toLowerCase() : template.defaultAccent,
        radius: Math.min(RADIUS_MAX, Math.max(RADIUS_MIN, radius)),
        iconStyle: ICON_STYLES.includes(stored.iconStyle) ? stored.iconStyle : 'chevron',
        headFont: HEAD_FONTS.includes(stored.headFont) ? stored.headFont : 'auto',
        theme: THEMES.includes(stored.theme) ? stored.theme : 'light',
        animate: stored.animate !== false,
        // A full section is the whole FAQ by category, so grouping is what it
        // is for; a hand-picked list reads as one list unless asked otherwise.
        groupByCategory: typeof stored.groupByCategory === 'boolean' ? stored.groupByCategory : sectionType === 'full',
        contactUrl: safeLink(stored.contactUrl),
    };
}

function varBlock(accent, dark) {
    return [
        `--fq-accent:${accent}`,
        `--fq-soft:${hexToRgba(accent, dark ? 0.22 : 0.11)}`,
        `--fq-ink:${dark ? '#f2f5fa' : '#0b2a46'}`,
        `--fq-sub:${dark ? 'rgba(222, 231, 243, 0.72)' : '#5b6b7e'}`,
        `--fq-border:${dark ? 'rgba(255, 255, 255, 0.14)' : '#e6e9f0'}`,
        `--fq-card:${dark ? 'rgba(255, 255, 255, 0.06)' : '#ffffff'}`,
    ].join(';');
}

/** The stylesheet URL for the webfonts a render needs, or '' for none. */
export function fontStylesheetUrl(fonts) {
    const families = (fonts || []).map((font) => FONT_FAMILIES[font]).filter(Boolean);
    return families.length ? `https://fonts.bunny.net/css?family=${families.join('|')}&display=swap` : '';
}

const ANSWER_PLACEHOLDER = /<!--hcfq-a:(\d+)-->/g;
const ID_ATTRIBUTES = /\b(id|for|aria-controls|aria-labelledby|aria-describedby)="([^"]*)"/g;

/**
 * Render a section with a template.
 *
 * @param {Object} content
 * @param {string} content.title            plain text
 * @param {string} [content.subtitle]       plain text
 * @param {boolean} [content.showHeading]   false hides the title and subtitle
 * @param {Array<{id: (number|string|null), question: string, answerHtml: string, category?: (string|null)}>} content.items
 *        `answerHtml` must already be sanitised: it is inserted as is
 * @param {Object<string, string>} [content.labels]  translated fixed copy
 * @param {string} [content.helpCenterUrl]  where Beacon's card points without a contact link
 * @param {string} [content.hostLabel]      the host Terminal's prompt names
 * @param {string} templateId
 * @param {Object} [design]                 the stored design; see resolveDesign()
 * @param {Object} options
 * @param {string} options.rootId           unique on the page
 * @param {'embed'|'preview'} [options.mode]
 * @param {boolean} [options.forceDark]     preview only: simulate a dark page for the `auto` theme
 * @param {'design'|'help-center'} [options.colorScheme]  `help-center` follows the v2 help center
 *        the section is shown in (`html[data-theme]`, light/dark/auto) instead of the design's own
 * @param {'custom'|'full'} [options.sectionType]
 * @param {string[]} [options.classNames]   extra classes on the root, e.g. a stable hook for custom CSS
 * @returns {{html: string, css: string, features: string[], fonts: string[], template: Object, design: Object}}
 */
export function renderFaqSection(content, templateId, design, options) {
    const template = getTemplate(templateId);
    const resolved = resolveDesign(template, design, options.sectionType);
    const rootId = options.rootId;
    const R = `.${rootId}`;
    const mode = options.mode || 'embed';

    // `auto` follows the visitor's colour scheme on a real page. A preview
    // shows a chosen page background instead, so it resolves `auto` itself.
    // Inside a help center the section takes the help center's own mode: the
    // design was chosen for the customer's site, not for this page.
    const followsHelpCenter = options.colorScheme === 'help-center' && !template.darkDefault;
    const dark = template.darkDefault || (!followsHelpCenter && (mode === 'preview' && resolved.theme === 'auto'
        ? !!options.forceDark
        : resolved.theme === 'dark'));
    const auto = !template.darkDefault && !followsHelpCenter && resolved.theme === 'auto' && mode !== 'preview';

    const headKey = resolved.headFont === 'auto' ? (DEFAULT_HEAD[template.id] || 'system') : resolved.headFont;
    const labels = { ...DEFAULT_LABELS, ...((content.labels && typeof content.labels === 'object') ? content.labels : {}) };
    const t = (key, replacements = {}) => escapeHtml(Object.keys(replacements).reduce(
        (text, name) => text.split(`:${name}`).join(String(replacements[name])),
        typeof labels[key] === 'string' ? labels[key] : DEFAULT_LABELS[key] || '',
    ));

    // ---- items ----
    const source = Array.isArray(content.items) ? content.items : [];
    const all = source.map((item, idx) => ({
        id: item.id ?? null,
        q: escapeHtml(item.question || ''),
        aHtml: `<!--hcfq-a:${idx}-->`,
        searchText: escapeHtml(`${item.question || ''} ${htmlToText(item.answerHtml)}`.toLowerCase()),
        num: String(idx + 1).padStart(2, '0'),
        cat: escapeHtml(item.category || labels.general || DEFAULT_LABELS.general),
        idx,
    }));

    // An FAQ filed in two categories arrives twice in a full section. Grouped
    // by category it belongs in both; in one flat list, once.
    const seen = new Set();
    const items = all.filter((item) => {
        if (item.id === null) {
            return true;
        }
        const key = String(item.id);
        if (seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });

    const useCats = template.catSupport === 'required'
        || (template.catSupport === 'optional' && resolved.groupByCategory);
    const catOrder = [];
    all.forEach((item) => {
        if (!catOrder.includes(item.cat)) {
            catOrder.push(item.cat);
        }
    });
    const cats = catOrder.map((name) => {
        const catItems = all.filter((item) => item.cat === name);
        return { name, items: catItems, count: catItems.length };
    });
    const groups = useCats ? cats.map((cat) => ({ name: cat.name, items: cat.items })) : [{ name: null, items }];

    const ctx = {
        mode,
        title: escapeHtml(content.title || ''),
        subtitle: escapeHtml(content.subtitle || ''),
        items,
        groups,
        cats: useCats ? cats : [{ name: escapeHtml(labels.general || DEFAULT_LABELS.general), items, count: items.length }],
        dark,
        animate: resolved.animate,
        v: {
            ink: 'var(--fq-ink)', sub: 'var(--fq-sub)', border: 'var(--fq-border)',
            card: 'var(--fq-card)', accent: 'var(--fq-accent)', soft: 'var(--fq-soft)',
            head: 'var(--fq-head)', serif: 'var(--fq-serif)', mono: 'var(--fq-mono)',
            radius: 'var(--fq-radius)', radiusCard: 'var(--fq-radius-card)', radiusWrap: 'var(--fq-radius-wrap)',
        },
        tokens: { accent: resolved.accent, radius: resolved.radius },
        rgba: hexToRgba,
        icon: ICONS[resolved.iconStyle] ?? ICONS.chevron,
        showIcon: resolved.iconStyle !== 'none',
        svg: MISC_SVG,
        reveal: (i) => (resolved.animate ? ` data-rv style="--fq-d:${Math.min(i, 8) * 70}ms"` : ''),
        t,
        contactHref: escapeHtml(resolved.contactUrl || safeLink(content.helpCenterUrl) || '#'),
        contactCta: resolved.contactUrl ? t('contactCta') : t('helpCenterCta'),
        hostLabel: escapeHtml(content.hostLabel || 'help'),
    };

    const out = template.render(ctx);

    // ---- HTML: scope the template's ids, then place the answers ----
    const answers = source.map((item) => (typeof item.answerHtml === 'string' ? item.answerHtml : ''));
    const body = out.html
        .replace(ID_ATTRIBUTES, (match, attribute, value) => `${attribute}="${rootId}-${value}"`)
        .replace(ANSWER_PLACEHOLDER, (match, idx) => `<div class="hcfq-answer" data-hcfq-item="${idx}">${answers[Number(idx)] ?? ''}</div>`);

    const classNames = [rootId, 'hc-faq-template', `hc-faq-template--${template.id}`, ...(options.classNames || [])]
        .map((name) => escapeHtml(name))
        .join(' ');
    const html = `<section class="${classNames}" id="${escapeHtml(rootId)}" data-template="${template.id}" aria-label="${ctx.title}">\n${body}\n</section>`;

    // ---- CSS ----
    const rootFont = headKey === 'page' ? 'inherit' : FONTS.system;
    const headFont = headKey === 'page' ? 'inherit' : FONTS[headKey];
    const base = `
${R}{${varBlock(resolved.accent, dark)};--fq-head:${headFont};--fq-serif:${FONTS.serif};--fq-mono:${FONTS.mono};--fq-radius:${resolved.radius}px;--fq-radius-card:${Math.round(resolved.radius * 1.3)}px;--fq-radius-wrap:${Math.round(resolved.radius * 1.8)}px;display:block;font-family:${rootFont};margin:0;padding:0;box-sizing:border-box;color:var(--fq-ink);font-size:16px;line-height:1.5;text-align:left}
${R} *,${R} *::before,${R} *::after{box-sizing:border-box}
${R} [hidden]{display:none!important}
${R} :where(h1,h2,h3,h4,h5,h6,p,ul,ol,li,dl,dt,dd,blockquote,figure,label){font-size:inherit;line-height:inherit;font-family:inherit;color:inherit;text-transform:none;letter-spacing:inherit;text-align:inherit;text-decoration:none;text-shadow:none;background:none;border:0;box-shadow:none;margin:0;padding:0}
${R} button{font:inherit;background:none;border:0;margin:0;padding:0;color:inherit;text-align:left;cursor:pointer;text-transform:none;letter-spacing:normal;box-shadow:none;width:auto;height:auto;min-width:0;min-height:0;border-radius:0}
${R} input{font:inherit;margin:0;box-shadow:none;height:auto;min-height:0;max-width:100%;text-transform:none}
${R} button:focus-visible,${R} input:focus-visible,${R} a:focus-visible{outline:2px solid var(--fq-accent);outline-offset:2px;border-radius:4px}
${R} .fq-ic{width:1em;height:1em;flex:0 0 auto;transition:transform .32s cubic-bezier(.4,0,.2,1)}
${R} .fq-svg{width:1em;height:1em;flex:0 0 auto}
${R} .fq-open .fq-ic{transform:${ICON_OPEN_TRANSFORM[resolved.iconStyle] ?? 'rotate(180deg)'}}
${R} .fq-panel{display:grid;grid-template-rows:0fr;transition:grid-template-rows .36s cubic-bezier(.4,0,.2,1)}
${R} .fq-open>.fq-panel,${R} .fq-open .fq-panel{grid-template-rows:1fr}
${R} .fq-panel>div{overflow:hidden;min-height:0}
${R} .fq-acc:not(.fq-open) .fq-panel>div{visibility:hidden;transition:visibility 0s .36s}
${R} .fq-a p{margin:0 0 10px}
${R} .fq-a p:last-child{margin-bottom:0}
${R} .fq-a ul,${R} .fq-a ol{margin:0 0 10px;padding-left:20px}
${R} .fq-a ul{list-style:disc}
${R} .fq-a ol{list-style:decimal}
${R} .fq-a li{display:list-item}
${R} .fq-a a{color:var(--fq-accent)}
${R} .fq-a code{font-family:var(--fq-mono);font-size:.88em;background:var(--fq-soft);padding:1px 6px;border-radius:5px}
${R} .hcfq-answer>:first-child{margin-top:0}
${R} .hcfq-answer>:last-child{margin-bottom:0}
${R} .fq-a h1,${R} .fq-a h2,${R} .fq-a h3,${R} .fq-a h4,${R} .fq-a h5,${R} .fq-a h6{margin:14px 0 6px;font-family:inherit;font-size:1.05em;font-weight:650;line-height:1.35;color:inherit;letter-spacing:normal;text-transform:none}
${R} .fq-a img,${R} .fq-a video{max-width:100%;height:auto}
${R} .fq-a iframe{max-width:100%;border:0}
${R} .fq-a figure{margin:0 0 10px}
${R} .fq-a blockquote{margin:0 0 10px;padding-left:12px;border-left:3px solid var(--fq-border)}
${R} .fq-a pre{margin:0 0 10px;padding:10px 12px;overflow:auto;white-space:pre-wrap;font-family:var(--fq-mono);font-size:.88em;background:var(--fq-soft);border-radius:6px}
${R} .fq-a table{width:100%;margin:0 0 10px;border-collapse:collapse;font-size:.95em}
${R} .fq-a th,${R} .fq-a td{padding:6px 8px;border:1px solid var(--fq-border);text-align:left;vertical-align:top}
${content.showHeading === false ? `${R} .fq-title,${R} .fq-sub,${R} .fq-mark,${R} .fq-kicker,${R} .fq-orn,${R} .fq-head:not(:has(> :not(.fq-title,.fq-sub,.fq-mark,.fq-kicker,.fq-orn))){display:none!important}` : ''}
${resolved.animate ? `${R} [data-rv]{opacity:0;transform:translateY(14px)}
${R} [data-rv].fq-in{opacity:1;transform:none;transition:opacity .5s cubic-bezier(.2,.7,.3,1) var(--fq-d,0ms),transform .5s cubic-bezier(.2,.7,.3,1) var(--fq-d,0ms)}` : ''}
@media (prefers-reduced-motion: reduce){${R} *{animation:none!important;transition:none!important}${R} [data-rv]{opacity:1;transform:none}}
`;

    let templateCss = out.css.split('.FQM').join(R);
    const darkCss = out.cssDark && !template.darkDefault ? out.cssDark.split('.FQM').join(R) : '';
    if (darkCss && dark) {
        templateCss += `\n${darkCss}`;
    } else if (darkCss && auto) {
        templateCss += `\n@media (prefers-color-scheme: dark){${darkCss}}`;
    }
    let schemeCss = auto ? `\n@media (prefers-color-scheme: dark){${R}{${varBlock(resolved.accent, true)}}}` : '';
    if (followsHelpCenter) {
        // The v2 help center's own switch: `data-theme` on <html>, where
        // `auto` (and no attribute) defers to the visitor's system setting.
        const under = (prefix) => `${prefix} ${R}{${varBlock(resolved.accent, true)}}\n${darkCss.split(R).join(`${prefix} ${R}`)}`;
        schemeCss = `\n${under(':root[data-theme="dark"]')}\n@media (prefers-color-scheme: dark){${under(':root:not([data-theme="light"])')}}`;
    }
    const css = (base + templateCss + schemeCss).trim();

    const fonts = [];
    if (headKey === 'brand') {
        fonts.push('brand');
    }
    if (headKey === 'serif' || templateCss.includes('var(--fq-serif)')) {
        fonts.push('serif');
    }

    const features = resolved.animate ? template.features : template.features.filter((feature) => feature !== 'reveal');

    return { html, css, features, fonts, template, design: resolved };
}

/**
 * A whole document around a rendered section, for the dashboard's preview
 * iframe: the iframe gives the section a real viewport, so the templates'
 * own breakpoints and the page background behave as they will on a site.
 *
 * @param {{html: string, css: string, fonts: string[]}} rendered
 * @param {{background?: string, padding?: string, customCss?: string, lang?: string}} [options]
 */
export function buildPreviewDocument(rendered, options = {}) {
    const fontsHref = fontStylesheetUrl(rendered.fonts);
    return `<!doctype html>
<html lang="${escapeHtml(options.lang || 'en')}">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
${fontsHref ? `<link rel="stylesheet" href="${escapeHtml(fontsHref)}">` : ''}
<style>html,body{margin:0;padding:0;background:${options.background || '#ffffff'}}body{padding:${options.padding || '32px 24px'};font-family:system-ui,-apple-system,'Segoe UI',Roboto,sans-serif}
${rendered.css}</style>
${options.customCss ? `<style data-hcfq-custom-css>${String(options.customCss).replace(/<\/style/gi, '<\\/style')}</style>` : ''}
</head>
<body>
${rendered.html}
</body>
</html>`;
}
